<div class="agpr-agentSidebar" [class.agpr-agentSidebar--adHocOpen]="showAdHocHistory">
    <!-- Which agent is open, and a way to swap to another without going back to the list -->
    <!-- The whole card opens the list, not just the name: only the edit button, which stops the
         click, and the list itself do anything else -->
    <div
        class="agpr-agentSidebar-header agpr-contextMenu-parent"
        role="button"
        tabindex="0"
        [attr.aria-expanded]="showAgentDropdown"
        [attr.aria-label]="'AutomationAgents.agent.toggleDropdown' | localize"
        (click)="toggleAgentDropdown()"
        (keydown.enter)="toggleAgentDropdown()"
        (keydown.space)="toggleAgentDropdown()"
        (clickOutside)="closeAgentDropdown()"
    >
        <!-- eslint-disable @angular-eslint/template/interactive-supports-focus -->
        <nav class="agpr-agentSidebar-headerGroup">
            <h1 [pTooltip]="agent?.name" tooltipPosition="bottom">{{ agent?.name }}</h1>

            <!-- Revealed on hover, as the card's controls are -->
            <div class="agpr-contextMenu agpr-agentSidebar-headerEdit">
                <agpr-button
                    id="agentSidebar.editAgent"
                    [style]="buttonTypes.contextMenu"
                    icon="fa-regular fa-duotone fa-pen"
                    tooltip="AutomationAgents.agent.edit"
                    tooltipPosition="bottom"
                    [ariaLabel]="'AutomationAgents.agent.edit' | localize"
                    (click)="editAgent($event)"
                ></agpr-button>
            </div>
        </nav>
        <div>
            <span class="agpr-agentSidebar-headerArrow fa-solid fa-angle-right"></span>

            @if (showAgentDropdown) {
                <!-- Inside the card, so its own clicks have to stop before they reach it -->
                <div class="agpr-agentSidebar-dropdown" (click)="$event.stopPropagation()">
                    <agpr-scroll-panel id="agentSidebar.agentsScroll" class="agpr-agentSidebar-dropdownScroll">
                        <ul
                            id="agentSidebar_agentList"
                            class="agpr-agentSidebar-dropdownList"
                            agprListKeyboardNavigation
                            [focusOnChild]="true"
                        >
                            @for (option of agents$ | async; track option.id) {
                                <li class="agpr-agentSidebar-dropdownItem">
                                    <div
                                        class="agpr-agentSidebar-dropdownIndicator"
                                        [class.agpr-agentSidebar-dropdownIndicator--current]="option.id === agent?.id"
                                    ></div>
                                    <div
                                        class="agpr-agentSidebar-dropdownAgent"
                                        tabindex="0"
                                        [routerLink]="agentRoute(option)"
                                        (click)="closeAgentDropdown()"
                                    >
                                        <span
                                            class="agpr-agentSidebar-dropdownName"
                                            [pTooltip]="option.name"
                                            tooltipPosition="right"
                                        >{{ option.name }}</span>
                                    </div>
                                </li>
                            }
                        </ul>
                    </agpr-scroll-panel>

                    <div class="agpr-agentSidebar-dropdownDivider"></div>
                    <a class="agpr-agentSidebar-dropdownMenuItem" [routerLink]="managementRoute">
                        {{ 'AutomationAgents.agent.manageAgents' | localize }}
                    </a>
                </div>
            }
        </div>
        <!-- eslint-enable @angular-eslint/template/interactive-supports-focus -->
    </div>

    <ul class="agpr-agentSidebar-list">
        <li
            id="agentSidebar_dashboard"
            class="agpr-agentSidebar-listItem"
            [routerLink]="sectionRoute(sections.dashboard)"
            routerLinkActive="agpr-agentSidebar-listItem--active"
            tabindex="0"
            (focus)="closeAgentDropdown()"
        >
            <span class="agpr-agentSidebar-listItemIcon fa-regular fa-duotone fa-gauge-high" aria-hidden="true"></span>
            <h2>{{ 'AutomationAgents.agent.dashboard' | localize }}</h2>
        </li>
        <li
            id="agentSidebar_mentions"
            class="agpr-agentSidebar-listItem"
            [routerLink]="sectionRoute(sections.mentions)"
            routerLinkActive="agpr-agentSidebar-listItem--active"
            tabindex="0"
        >
            <span class="agpr-agentSidebar-listItemIcon fa-regular fa-duotone fa-newspaper" aria-hidden="true"></span>
            <h2>{{ 'AutomationAgents.agent.mentions' | localize }}</h2>
        </li>
        <li
            id="agentSidebar_analytics"
            class="agpr-agentSidebar-listItem"
            [routerLink]="sectionRoute(sections.analytics)"
            routerLinkActive="agpr-agentSidebar-listItem--active"
            tabindex="0"
        >
            <span class="agpr-agentSidebar-listItemIcon fa-regular fa-duotone fa-chart-mixed" aria-hidden="true"></span>
            <h2>{{ 'AutomationAgents.agent.analytics' | localize }}</h2>
        </li>
    </ul>

    <div class="agpr-agentSidebar-rule"></div>

    <div class="agpr-agentSidebar-sources" role="group" [attr.aria-label]="'AutomationAgents.agent.sources' | localize">
        <div class="agpr-agentSidebar-sourcesHeader">
            <h3 class="agpr-agentSidebar-sourcesTitle">{{ 'AutomationAgents.agent.sources' | localize }}</h3>
            <agpr-button
                id="agentSidebar.addSource"
                [style]="buttonTypes.icon"
                icon="fa-regular fa-duotone fa-square-plus"
                tooltip="AutomationAgents.agent.addSource"
                tooltipPosition="left"
                [ariaLabel]="'AutomationAgents.agent.addSource' | localize"
                (click)="addSource()"
            ></agpr-button>
        </div>

        <agpr-scroll-panel id="agentSidebar.sourcesScroll" class="agpr-agentSidebar-sourcesScroll">
            <ul class="agpr-agentSidebar-sourceList">
                @for (source of sources; track source.id; let i = $index) {
                    <li class="agpr-agentSidebar-sourceItem agpr-contextMenu-parent">
                        <agpr-checkbox
                            [id]="'agentSidebar_source_' + i"
                            class="agpr-agentSidebar-sourceCheckbox"
                            [binary]="true"
                            [hiddenLabel]="sourceName(source)"
                            [(ngModel)]="selectedSourceIds[source.id]"
                            [disabled]="isLastSelectedSource(source)"
                            (ngModelChange)="sourceSelectionChanged()"
                        ></agpr-checkbox>
                        <div class="agpr-agentSidebar-sourceLabel">
                            <span
                                class="agpr-agentSidebar-sourceIcon"
                                [ngClass]="sourceIcon(source)"
                                [pTooltip]="sourceTypeLabel(source) | localize"
                                tooltipPosition="top"
                                [attr.aria-label]="sourceTypeLabel(source) | localize"
                            ></span>
                            <span class="agpr-agentSidebar-sourceName" [pTooltip]="sourceName(source)">
                                {{ sourceName(source) }}
                            </span>
                        </div>

                        <span
                            class="agpr-agentSidebar-sourceStatus"
                            [class.agpr-agentSidebar-sourceStatus--paused]="!source.isActive"
                            [class.agpr-agentSidebar-sourceStatus--error]="source.hasError"
                            [class.agpr-agentSidebar-sourceStatus--standby]="source.isStandby"
                        >{{ sourceStatusLabel(source) | localize }}</span>

                        <agpr-split-button
                            class="agpr-contextMenu agpr-agentSidebar-sourceMenu"
                            [id]="'agentSidebar_source_' + i + '_actions'"
                            [style]="buttonTypes.icon"
                            [showTriggerIcon]="false"
                            icon="fa-light fa-ellipsis-vertical"
                            [options]="sourceMenus.get(source.id)"
                            [data]="source"
                            panelPositionX="right"
                        ></agpr-split-button>
                    </li>
                }

                <!-- Mentions from sources the agent no longer has, which have no row of their own -->
                <li class="agpr-agentSidebar-sourceItem agpr-agentSidebar-otherItem">
                    <agpr-checkbox
                        id="agentSidebar_other"
                        class="agpr-agentSidebar-sourceCheckbox"
                        [binary]="true"
                        [hiddenLabel]="'Monitoring.filtersLocalization.aiAgents.other' | localize"
                        [(ngModel)]="otherSelected"
                        [disabled]="isLastSelectedOther"
                        (ngModelChange)="otherSelectionChanged()"
                    ></agpr-checkbox>
                    <span class="agpr-agentSidebar-sourceName">
                        {{ 'Monitoring.filtersLocalization.aiAgents.other' | localize }}
                    </span>
                    <span
                        class="agpr-agentSidebar-otherInfo fa-regular fa-circle-info"
                        role="img"
                        [pTooltip]="'Monitoring.filtersLocalization.aiAgents.other.tooltip' | localize"
                        tooltipPosition="top"
                        [attr.aria-label]="'Monitoring.filtersLocalization.aiAgents.other.tooltip' | localize"
                    ></span>
                </li>

                <!-- Ad hoc submissions are not sources, but they are filtered alongside them -->
                @if (adHocEvents.length) {
                    <li class="agpr-agentSidebar-sourceItem agpr-agentSidebar-adHocItem">
                        <agpr-checkbox
                            id="agentSidebar_adHocEvents"
                            class="agpr-agentSidebar-sourceCheckbox"
                            [binary]="true"
                            [hiddenLabel]="'AutomationAgents.agent.adHocEvents' | localize"
                            [ngModel]="allAdHocSelected"
                            [partialSelected]="someAdHocSelected"
                            [disabled]="adHocGroupLocked"
                            (ngModelChange)="adHocGroupChanged($event)"
                        ></agpr-checkbox>
                        <span class="agpr-agentSidebar-sourceName">
                            {{ 'AutomationAgents.agent.adHocEvents' | localize }}
                        </span>

                        <!-- eslint-disable-next-line @angular-eslint/template/interactive-supports-focus -->
                        <agpr-button
                            class="agpr-agentSidebar-adHocArrow"
                            [ariaLabel]="'AutomationAgents.agent.adHocEvents.history'"
                            [style]="buttonTypes.icon"
                            [icon]="'fa-solid fa-angle-right'"
                            (click)="toggleAdHocHistory()"
                        ></agpr-button>

                    </li>
                }
            </ul>
        </agpr-scroll-panel>
    </div>
    <!-- Outside the sources list: it is a flyout beside the panel, and the list's scroll area would
         clip it -->
    @if (showAdHocHistory) {
        <div class="agpr-agentSidebar-adHocHistory" (clickOutside)="closeAdHocHistory()">
            <div class="agpr-agentSidebar-adHocHistoryHeader">
                <h4 class="agpr-agentSidebar-adHocHistoryTitle">
                    {{ 'AutomationAgents.agent.adHocEvents.history' | localize }}
                </h4>
                <span class="agpr-agentSidebar-adHocHistoryCount">
                    {{ 'AutomationAgents.agent.adHocEvents.count' | localize: { count: adHocEvents.length } }}
                </span>
            </div>

            <agpr-scroll-panel id="agentSidebar.adHocScroll" class="agpr-agentSidebar-adHocHistoryScroll">
                <ul class="agpr-agentSidebar-adHocList">
                    @for (event of adHocEvents; track event.id; let index = $index) {
                        <li class="agpr-agentSidebar-adHocEvent">
                            <agpr-checkbox
                                [id]="'agentSidebar_adHocEvent_' + index"
                                [label]="event.createdAt | changeTimeZone: (userTimeZone$ | async) : 'MMMM Do, YYYY HH:mm z'"
                                [binary]="true"
                                [(ngModel)]="selectedAdHocIds[event.id]"
                                [disabled]="isLastSelectedAdHocEvent(event)"
                                (ngModelChange)="adHocSelectionChanged()"
                            ></agpr-checkbox>
                        </li>
                    }
                </ul>
            </agpr-scroll-panel>
        </div>
    }
</div>
